import { useCallback } from "react";
import { Card } from "@/frontend/components/common";
import { FaListAlt, FaUsers } from "react-icons/fa";
import type { FormatTabProps } from "../../types";
import type { StageConfig } from "@/core/value-objects/StageConfig";
import { StagesEditor } from "./StagesEditor";
import { TeamsRoster } from "./TeamsRoster";
import { GroupAssignmentBoard } from "./GroupAssignmentBoard";
import { KnockoutAssignmentBoard } from "./KnockoutAssignmentBoard";

export function FormatTab({
  tournamentId,
  currentSeason,
  tournamentStages = [],
  onStagesChange,
}: FormatTabProps) {
  const renderGroupAssignment = useCallback(
    (
      stage: StageConfig,
      onManualSetupChange?: (setup: {
        type: "GROUPS";
        stageOrder?: number;
        teamsPerGroup: number;
        groups: Array<{ id: string; name: string }>;
        assignments: Record<string, string | null>;
      }) => void,
    ) => (
      <GroupAssignmentBoard
        tournamentId={tournamentId}
        seasonId={currentSeason.id}
        formatId={currentSeason.formatId}
        stage={stage}
        compact
        tournamentStages={tournamentStages}
        onManualSetupChange={onManualSetupChange}
      />
    ),
    [currentSeason.id, currentSeason.formatId, tournamentId, tournamentStages]
  );
  const renderKnockoutAssignment = useCallback(
    (stage: StageConfig) => (
      <KnockoutAssignmentBoard
        tournamentId={tournamentId}
        seasonId={currentSeason.id}
        formatId={currentSeason.formatId}
        stage={stage}
      />
    ),
    [currentSeason.id, currentSeason.formatId, tournamentId]
  );

  if (!currentSeason || !currentSeason.formatId) {
    return (
      <div className="py-4">
        <Card className="shadow-sm">
          <div className="text-center py-8">
            <p className="text-gray-500">
              Esta temporada no tiene un formato asignado todavía.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="py-4 space-y-6">
      <Card className="shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <FaListAlt className="text-indigo-500" />
          <h3 className="text-lg font-semibold text-gray-700">Formato y Reglas</h3>
        </div>
        <StagesEditor
          formatId={currentSeason.formatId}
          seasonId={currentSeason.id}
          onStagesChange={onStagesChange}
          renderGroupAssignment={renderGroupAssignment}
          renderKnockoutAssignment={renderKnockoutAssignment}
        />
      </Card>

      <Card className="shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <FaUsers className="text-blue-500" />
          <h3 className="text-lg font-semibold text-gray-700">Equipos de la Temporada</h3>
        </div>
        <TeamsRoster tournamentId={tournamentId} seasonId={currentSeason.id} />
      </Card>
    </div>
  );
}
